// VoiceCloningTool.jsx
// Main component that manages voice cloning features: voice sample creation, TTS generation, and voice management

import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  Alert,
  Stack,
  Grid,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Divider,
  Badge,
  Tooltip
} from '@mui/material';
import {
  RecordVoiceOver as VoiceIcon,
  VolumeUp as TTSIcon,
  Analytics as AnalyticsIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Upgrade as UpgradeIcon,
  Add as AddIcon,
  Close as CloseIcon
} from '@mui/icons-material';

// Import sub-components
import VoiceSampleUpload from './voice/VoiceSampleUpload';
import TTSGenerator from './voice/TTSGenerator';

// Enhanced VoiceManagement with TTS History - import it here directly
import VoiceManagementWithHistory from './voice/VoiceManagementWithHistory';

const VoiceCloningTool = ({ 
  businessId, 
  businessName, 
  isBusinessOwner,
  accountType = 'free',
  voiceLibrary = null,
  quotaUsage = null,
  existingVoice = null,
  onVoiceSuccess, 
  onCancel,
  onError 
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedVoice, setSelectedVoice] = useState(existingVoice);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showTTSDialog, setShowTTSDialog] = useState(false);

  const token = useSelector((state) => state.auth?.token);

  // Voice cloning quotas and limits
  const quotaLimits = {
    free: { 
      maxSamples: 1, 
      maxTTSChars: 500,
      description: 'Free Plan'
    },
    premium: { 
      maxSamples: 5, 
      maxTTSChars: 5000,
      description: 'Premium Plan'
    },
    enterprise: { 
      maxSamples: -1, 
      maxTTSChars: 20000,
      description: 'Enterprise Plan'
    }
  };

  const currentLimits = quotaLimits[accountType] || quotaLimits.free;

  // Get current voice usage
  const voiceUsage = {
    samplesCreated: voiceLibrary?.voices?.length || 0,
    ttsCharactersUsed: quotaUsage?.voice_tts_generation?.lifetime || quotaUsage?.voice_tts_generation?.totalCharacters || 0,
    monthlyTtsUsage: (() => {
      const now = new Date();
      const currentMonthKey = `${now.getFullYear()}-${now.getMonth() + 1}`;
      const ttsQuota = quotaUsage?.voice_tts_generation || {};
      return ttsQuota.monthly?.[currentMonthKey] || ttsQuota.monthly?.[`${currentMonthKey}_characters`] || 0;
    })()
  };

  console.log('voiceUsage', voiceUsage)
  console.log('quotaUsage', quotaUsage)

  // Check if user can create new voices (separate from TTS usage)
  const canCreateVoice = currentLimits.maxSamples === -1 || voiceUsage.samplesCreated < currentLimits.maxSamples;
  
  // TTS should work as long as: 1) have active voices, 2) haven't exceeded monthly TTS limit
  const canUseTTS = voiceUsage.monthlyTtsUsage < currentLimits.maxTTSChars;

  // Get available voices
  const availableVoices = voiceLibrary?.voices?.filter(voice => voice.status === 'active') || [];

  useEffect(() => {
    // Auto-select the first active voice if none selected
    if (!selectedVoice && availableVoices.length > 0) {
      setSelectedVoice(availableVoices[0]);
    }
  }, [availableVoices, selectedVoice]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <CheckIcon color="success" />;
      case 'processing': return <ScheduleIcon color="warning" />;
      case 'failed': return <ErrorIcon color="error" />;
      default: return <WarningIcon color="disabled" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'processing': return 'warning';
      case 'failed': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleVoiceCreated = (newVoiceData) => {
    // Update the selected voice to the newly created one
    setSelectedVoice(newVoiceData);
    setShowCreateDialog(false);
    onVoiceSuccess && onVoiceSuccess(newVoiceData);
  };

  const handleTTSGenerated = (ttsResult) => {
    // Handle TTS generation success
    // Could trigger a callback or show success message
    console.log('TTS generation completed:', ttsResult);
  };

  const handleRefresh = () => {
    // Trigger refresh from parent component to update voice data
    console.log('Refreshing voice data...');
    onVoiceSuccess && onVoiceSuccess();
  };

  // Tab panels
  const VoiceOverviewPanel = () => (
    <Box>
      {/* Account Info */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2">
            <strong>{currentLimits.description}</strong> • 
            {currentLimits.maxSamples === -1 ? ' Unlimited voices' : ` ${currentLimits.maxSamples} voice samples`} • 
            {currentLimits.maxTTSChars.toLocaleString()} TTS characters per month
          </Typography>
          {accountType === 'free' && (
            <Button size="small" startIcon={<UpgradeIcon />} variant="outlined">
              Upgrade
            </Button>
          )}
        </Box>
      </Alert>

      {/* Quick Actions */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <VoiceIcon color="primary" />
                <Typography variant="h6">Voice Samples</Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Create AI voice clones using your own voice recordings.
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2">
                  {voiceUsage.samplesCreated}/{currentLimits.maxSamples === -1 ? '∞' : currentLimits.maxSamples} created
                </Typography>
                <Chip 
                  label={`${availableVoices.length} Active`} 
                  size="small" 
                  color="success" 
                />
              </Box>
              
              <Button
                variant="contained"
                fullWidth
                startIcon={<AddIcon />}
                onClick={() => setShowCreateDialog(true)}
                disabled={!canCreateVoice}
              >
                {canCreateVoice ? 'Create Voice Sample' : 'Voice Limit Reached'}
              </Button>
              
              {!canCreateVoice && accountType === 'free' && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                  Upgrade to Premium for more voice samples
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <TTSIcon color="primary" />
                <Typography variant="h6">Text-to-Speech</Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Generate speech using your AI voice clones.
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2">
                  {voiceUsage.monthlyTtsUsage.toLocaleString()}/{currentLimits.maxTTSChars.toLocaleString()} chars used
                </Typography>
                <Chip 
                  label={canUseTTS ? 'Available' : 'Limit Reached'} 
                  size="small" 
                  color={canUseTTS ? 'success' : 'error'} 
                />
              </Box>
              
              <Button
                variant="contained"
                fullWidth
                startIcon={<TTSIcon />}
                onClick={() => setShowTTSDialog(true)}
                disabled={availableVoices.length === 0 || !canUseTTS}
              >
                {availableVoices.length === 0 ? 'No Voices Available' :
                 !canUseTTS ? 'Monthly Character Limit Reached' :
                 'Generate Speech'}
              </Button>
              
              {availableVoices.length === 0 && (
                <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                  Create a voice sample first
                </Typography>
              )}
              
              {availableVoices.length > 0 && !canUseTTS && (
                <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block', textAlign: 'center' }}>
                  {voiceUsage.monthlyTtsUsage}/{currentLimits.maxTTSChars} characters used this month
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Current Voices */}
      {voiceLibrary?.voices && voiceLibrary.voices.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>Your Voice Library</Typography>
          
          <Stack spacing={2}>
            {voiceLibrary.voices.slice(0, 3).map((voice) => (
              <Card key={voice._id} variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <VoiceIcon color="primary" />
                      <Box>
                        <Typography variant="subtitle1">{voice.voiceName}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {voice.voiceDescription || 'No description'}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getStatusIcon(voice.status)}
                      <Chip 
                        label={voice.status} 
                        size="small" 
                        color={getStatusColor(voice.status)}
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                  
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Created {formatDate(voice.createdAt)} • Provider: {voice.provider || 'ElevenLabs'}
                  </Typography>
                  
                  {voice.status === 'active' && selectedVoice?._id !== voice._id && (
                    <Button
                      size="small"
                      onClick={() => setSelectedVoice(voice)}
                      sx={{ mt: 1 }}
                    >
                      Select for TTS
                    </Button>
                  )}
                  
                  {selectedVoice?._id === voice._id && (
                    <Chip 
                      label="Currently Selected" 
                      size="small" 
                      color="primary" 
                      sx={{ mt: 1 }}
                    />
                  )}
                </CardContent>
              </Card>
            ))}
            
            {voiceLibrary.voices.length > 3 && (
              <Typography variant="body2" color="text.secondary" align="center">
                +{voiceLibrary.voices.length - 3} more voices • View all in Voice Management tab
              </Typography>
            )}
          </Stack>
        </Box>
      )}

      {/* No Voices State */}
      {(!voiceLibrary?.voices || voiceLibrary.voices.length === 0) && (
        <Alert severity="info">
          <Typography variant="body2">
            <strong>Get Started with Voice Cloning</strong>
            <br />
            Create your first AI voice clone to enable text-to-speech generation for your business. 
            Perfect for customer service messages, announcements, and marketing content.
          </Typography>
        </Alert>
      )}
    </Box>
  );

  const TTSPanel = () => (
    <Box>
      {availableVoices.length === 0 ? (
        <Alert severity="warning">
          <Typography variant="body2">
            No active voices available for TTS generation. Please create a voice sample first.
          </Typography>
        </Alert>
      ) : !selectedVoice ? (
        <Alert severity="info">
          <Typography variant="body2">
            Please select a voice from your library to generate speech.
          </Typography>
        </Alert>
      ) : (
        <TTSGenerator
          businessId={businessId}
          businessName={businessName}
          voice={selectedVoice}
          accountType={accountType}
          onSuccess={handleTTSGenerated}
          onCancel={onCancel}
          onError={onError}
        />
      )}
    </Box>
  );

  // Updated VoiceManagementPanel with TTS History integration
  const VoiceManagementPanel = () => (
    <VoiceManagementWithHistory
      businessId={businessId}
      businessName={businessName}
      accountType={accountType}
      voiceLibrary={voiceLibrary}
      quotaUsage={quotaUsage}
      onVoiceUpdate={onVoiceSuccess}
      onRefresh={handleRefresh}
    />
  );

  // Don't render if not business owner
  if (!isBusinessOwner) return null;

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Voice Cloning & Text-to-Speech
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create AI voice clones and generate professional speech content for your business.
        </Typography>
      </Box>

      {/* Selected Voice Info */}
      {selectedVoice && (
        <Alert severity="success" sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box>
              <Typography variant="body2" fontWeight="medium">
                Active Voice: {selectedVoice.voiceName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedVoice.voiceDescription || 'Ready for TTS generation'}
              </Typography>
            </Box>
            <Chip 
              label={selectedVoice.provider || 'ElevenLabs'} 
              size="small" 
              color="primary" 
              variant="outlined" 
            />
          </Box>
        </Alert>
      )}

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VoiceIcon fontSize="small" />
                Overview
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TTSIcon fontSize="small" />
                Generate Speech
                {!canUseTTS && (
                  <Badge badgeContent="!" color="error" />
                )}
              </Box>
            } 
          />
          <Tab 
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <SettingsIcon fontSize="small" />
                Manage Voices
                {voiceLibrary?.voices && (
                  <Badge badgeContent={voiceLibrary.voices.length} color="primary" />
                )}
              </Box>
            } 
          />
        </Tabs>
      </Box>

      {/* Tab Content */}
      {activeTab === 0 && <VoiceOverviewPanel />}
      {activeTab === 1 && <TTSPanel />}
      {activeTab === 2 && <VoiceManagementPanel />}

      {/* Create Voice Sample Dialog */}
      <Dialog 
        open={showCreateDialog} 
        onClose={() => setShowCreateDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Create Voice Sample</Typography>
            <IconButton onClick={() => setShowCreateDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <VoiceSampleUpload
            businessId={businessId}
            businessName={businessName}
            accountType={accountType}
            existingVoice={null}
            onSuccess={handleVoiceCreated}
            onCancel={() => setShowCreateDialog(false)}
            onError={onError}
          />
        </DialogContent>
      </Dialog>

      {/* TTS Generation Dialog */}
      <Dialog 
        open={showTTSDialog} 
        onClose={() => setShowTTSDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Generate Speech</Typography>
            <IconButton onClick={() => setShowTTSDialog(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedVoice ? (
            <TTSGenerator
              businessId={businessId}
              businessName={businessName}
              voice={selectedVoice}
              accountType={accountType}
              onSuccess={handleTTSGenerated}
              onCancel={() => setShowTTSDialog(false)}
              onError={onError}
            />
          ) : (
            <Alert severity="warning">
              <Typography variant="body2">
                Please select a voice from your library first.
              </Typography>
            </Alert>
          )}
        </DialogContent>
      </Dialog>

      {/* Usage Tips */}
      <Alert severity="success" sx={{ mt: 3 }}>
        <Typography variant="body2">
          <strong>Pro Tips:</strong> 
          • Record 30-60 seconds of clear speech for best voice quality
          • Use emotional instructions in TTS for natural-sounding results
          • Generated audio is perfect for phone systems, websites, and marketing
          • Voice samples work best with your natural speaking voice
        </Typography>
      </Alert>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button onClick={onCancel}>
          Close
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {availableVoices.length === 0 && (
            <Button
              variant="contained"
              onClick={() => setShowCreateDialog(true)}
              disabled={!canCreateVoice}
              startIcon={<AddIcon />}
            >
              Create First Voice
            </Button>
          )}
          {availableVoices.length > 0 && (
            <Button
              variant="contained"
              onClick={() => setShowTTSDialog(true)}
              disabled={!canUseTTS || !selectedVoice}
              startIcon={<TTSIcon />}
            >
              Generate Speech
            </Button>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default VoiceCloningTool;